/**
 * This file is part of the Haiman.
 * @link     : https://haiman.io/
 * @author   : Emil Zhai (root@derzh.com)
 * @modifier : Emil Zhai (root@derzh.com)
 * @copyright: Copyright (c) 2018 Hangzhou Haila Network Technology Co., Ltd.
 */

import { replaceObjectKey } from '@/utils/transfer';
import http from './driver';
import { UserBasic } from './types/user';
import { SecretEmotion, SecretPost, MenuItem, SecretSearch, SecretGroupFilter, SecretPostFilter, SecretCommentPost, SecretTopic, SecretComment, GameItemRooms, SecretRoom, SecretAsrResult } from './types/secret';

export interface SecretNavData {
  /** 导航栏key */
  key: string;
  /** 导航栏列表 */
  items: MenuItem[];
}
export const getNav = (filter, silent) => http.get<SecretNavData>(`secret/navs/${filter}`, {}, { silent });
export const getPostFilterInfo = (filter, silent) => http.get<SecretPostFilter>(`secret/post-filters/${filter}`, {}, { silent });
export const getPostList = (filter, body) => http.get<SecretPost[]>(`secret/posts/${filter}`, body, { silent: true });
export const delPost = (id, extra, type) => http.delete(`secret/posts/${id}${extra ? `?extra=${extra}` : ''}${type ? `${extra ? '&' : '?'}type=${type}` : ''}`);
export const getPost = (id, withReport, silent) => http.get<SecretPost>(`secret/posts/${id}`, { withReport }, { silent });
export const newPost = ({
  content,
  numReward: num_reward,
  imageId: image_id,
  voiceId: voice_id,
  videoId: video_id,
  roomId: room_id,
  isFamous,
  isWall,
  lottery,
  survey,
}, opt = {}) => http.post('secret/posts', { content, num_reward, image_id, voice_id, video_id, room_id, isFamous, isWall, lottery, survey }, Object.assign({ modal: true }, opt));
export const getLucky = body => http.post<SecretPost>('secret/posts/lucky', body);

export const getComments = ({ filter, point, limit, kw, silent = true }) =>
  http.get<SecretComment[]>(`secret/comments/${filter}`, { point, limit, kw }, { silent });

export const getPostComments = ({ id, filter, point, limit, last, silent = true }) =>
  http.get<SecretCommentPost[]>(`secret/posts/${id}/comments`, { point, limit, filter, last }, { silent });

export const postCommentPost = (postId, {
  content,
  imageId: image_id,
  voiceId: voice_id,
  replyId: reply_id,
  isFamous,
  isAdmin,
  isPrivate,
}) => http.post(`secret/posts/${postId}/comments`, { content, image_id, voice_id, reply_id, isFamous, isAdmin, isPrivate }, { modal: true });
export const delComment = (id, extra) => http.delete(`secret/comments/${id}${extra ? `?extra=${extra}` : ''}`, {}, { modal: true });

export const deletePostRelations = id => http.delete(`secret/relations/post-${id}`, {}, { modal: true });
export const changePostRelations = (id, body) => http.put(`secret/relations/post-${id}`, body, { modal: true });
export const deleteCommentRelations = id => http.delete(`secret/relations/comment-${id}`, {}, { modal: true });
export const changeCommentRelations = (id, body) => http.put(`secret/relations/comment-${id}`, body, { modal: true });

export const postAsr = (type, id) => {
  const formData = new FormData();
  formData.append('type', type);
  formData.append('id', id);
  return http.post<SecretAsrResult>('secret/asr', formData);
};

export const getMenu = () => http.get('secret/menu/main');

export const getSearch = body => http.get<SecretSearch[]>('secret/search', replaceObjectKey(body, { kw: 'q' }));

export interface SecretTopMetaItem {
  /** 榜单名称 */
  label: string;
  /** 入口type */
  type: string;
  /** 榜单top3用户 */
  tops: SecretTopUser[];
}
export interface SecretTopUser {
  /** 用户信息 */
  user: UserBasic;
  /** 排序值 */
  value: string;
  /** 区服 */
  server: string;
}
export interface SecretTopMeta {
  /** 标题 */
  label: string;
  /** 榜单列表 */
  items: SecretTopMetaItem[];
}
export const getTopsTabs = type => http.get<SecretTopMeta>(`secret/tops-meta/${type}`);
export const getTopsMeta = () => http.get<SecretTopMeta>('secret/tops-meta/_index');
export const getTops = type => http.get<SecretTopUser[]>(`secret/tops/${type}`);

export const getTopics = (kw, limit, scene) => http.get<SecretTopic[]>('secret/topics', { kw, limit, scene }, { silent: true });
export const getTopic = id => http.get<SecretTopic>(`secret/topics/detail-${id}`);
export const putTopic = (id, body) => http.put(`secret/topics/detail-${id}`, body);
export const getEmotions = (silent = true) => http.get<SecretEmotion[]>('emotions', {}, { silent });
export const getInputTips = () => http.get<Record<string, string[]>>('secret/input-tips');
export const getSearchTips = ({ filter, q }) => http.get<string[]>('secret/search-tips', { q, filter }, { silent: true });
export const getRoomsAll = scene => http.get<GameItemRooms[]>('secret/rooms/all', { scene });
export const getRoom = id => http.get<SecretRoom>(`secret/rooms/${id}`);
export const putRoom = (id, body) => http.put(`secret/rooms/${id}`, body);
export const getGroupFilters = (group: string) => http.get<SecretGroupFilter[]>(`secret/group-filters/${group}`);
export const getStickyRules = () => http.get<string[]>('secret/sticky-rules');
